import * as React from 'react';

import { faCircleInfo, faFiles, faKey, faLink } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AccountingPackage, PortalTextImportModals } from '../../Models/Api/strongbox.financialportal';
import { ConnectorType, LinkMode } from '../../Store/ImportFinancials';

import { FormatAndedStringList } from '../../Utils/StringUtils';

type Props = {
    importType: LinkMode;
    onChangeImportType: (descriptor: LinkMode) => void;
    importModalsText?: PortalTextImportModals;
    connectorType: ConnectorType;
    // Real accounting-package definitions for the running consumer; drives the supported-systems banner.
    activeAccountingPackages?: AccountingPackage[];
    // When true, this screen is the flow entry point (neither legacy excel upload nor file upload is
    // enabled, so the connection-type step was skipped). In that case show a banner listing the supported
    // accounting systems at the top, since the user never saw the connection-type screen that lists them.
    showSupportedSystemsBanner?: boolean;
};

export const ChooseImportModeModal: React.FC<Props> = (props): React.ReactElement => {
    const {
        importType,
        onChangeImportType,
        importModalsText,
        connectorType,
        activeAccountingPackages,
        showSupportedSystemsBanner,
    } = props;

    // The universal collector / excel upload flows ("uc" variant) reword the cards and use a file-oriented
    // icon for the "connect directly" (upload files yourself) option.
    const isNative = connectorType === ConnectorType.fullIntegration;

    const shareableSelected = importType === LinkMode.shareable;
    const directSelected = importType === LinkMode.direct;

    // Supported accounting systems come from the real packages configured for the consumer (the same source
    // the connection-type screen uses), in the marketing order design expects — not a hard-coded list.
    // In practice note that friendlyName won't ever be undefined, checking for falsy on it is just a guard.
    const supportedAccountingSystems = React.useMemo(() => {
        return (activeAccountingPackages || [])
            .filter(pkg => pkg.packageType === 'thirdParty')
            .sort((p1, p2) => (p1.marketingSortOrder ?? 0) - (p2.marketingSortOrder ?? 0))
            .map(pkg => {
                const name = pkg.friendlyName;
                return name ? (pkg.isBeta ? `${name} (Beta)` : name) : '';
            })
            .filter(name => !!name);
    }, [activeAccountingPackages]);

    const showBanner = !!showSupportedSystemsBanner && isNative && supportedAccountingSystems.length > 0;

    return (
        <>
            {showBanner && (
                <div
                    className={'cr-notification cr-notification--primary cr-notification--banner'}
                    role={'note'}
                    style={{ width: '100%', marginBottom: 'var(--size-padding-medium)' }}
                >
                    <span className={'cr-notification__icon'}><FontAwesomeIcon icon={faCircleInfo} /></span>
                    <div className={'cr-notification__text'}>
                        <p className={'cr-notification__description'}>
                            <strong>{importModalsText?.supportedAccountingSystems || ''}:</strong>{' '}
                            {FormatAndedStringList(supportedAccountingSystems)}
                        </p>
                    </div>
                </div>
            )}
            <div className={'page-grid-2col'}>
                {/* Create link / secure URL */}
                <label className={'cr-selectable-card page-hero-card'}>
                    <div className={'page-hero-card__banner'}>
                        <span className={`page-hero-card__icon${shareableSelected ? ' page-hero-card__icon--active' : ''}`}>
                            <FontAwesomeIcon icon={faLink} />
                        </span>
                    </div>
                    <div className={'page-hero-card__body'}>
                        <span className={'cr-selectable-card__control'}>
                            <input
                                data-connection-type={`${connectorType === ConnectorType.universalConnector ? 'u2' : 'native-integration'}-create-upload-link`}
                                type={'radio'}
                                className={'cr-radio'}
                                name={'connection-method'}
                                id={'import-mode-shareable'}
                                checked={shareableSelected}
                                onChange={() => onChangeImportType(LinkMode.shareable)}
                            />
                        </span>
                        <span className={'cr-selectable-card__content page-hero-card__content'}>
                            <span className={'cr-selectable-card__header'}>
                                <span className={'cr-selectable-card__heading page-hero-card__heading'}>
                                    {(isNative ? importModalsText?.sendSecureURL : importModalsText?.sendSecureURLUniversal) || ''}
                                </span>
                            </span>
                            <span className={'cr-selectable-card__body'}>
                                <span className={'cr-selectable-card__description'}>
                                    {(isNative ? importModalsText?.sendSecureURLDescription : importModalsText?.sendSecureURLDescriptionUniversal) || ''}
                                </span>
                            </span>
                        </span>
                    </div>
                </label>

                {/* Connect directly / upload files yourself */}
                <label className={'cr-selectable-card page-hero-card'}>
                    <div className={'page-hero-card__banner'}>
                        <span className={`page-hero-card__icon${directSelected ? ' page-hero-card__icon--active' : ''}`}>
                            <FontAwesomeIcon icon={isNative ? faKey : faFiles} />
                        </span>
                    </div>
                    <div className={'page-hero-card__body'}>
                        <span className={'cr-selectable-card__control'}>
                            <input
                                data-connection-type={`${connectorType === ConnectorType.universalConnector ? 'u2' : 'native-integration'}-upload-files-yourself`}
                                type={'radio'}
                                className={'cr-radio'}
                                name={'connection-method'}
                                id={'import-mode-direct'}
                                checked={directSelected}
                                onChange={() => onChangeImportType(LinkMode.direct)}
                            />
                        </span>
                        <span className={'cr-selectable-card__content page-hero-card__content'}>
                            <span className={'cr-selectable-card__header'}>
                                <span className={'cr-selectable-card__heading page-hero-card__heading'}>
                                    {(isNative ? importModalsText?.connectDirectly : importModalsText?.connectDirectlyUniversal) || ''}
                                </span>
                            </span>
                            <span className={'cr-selectable-card__body'}>
                                <span className={'cr-selectable-card__description'}>
                                    {(isNative ? importModalsText?.connectDirectlyDescription : importModalsText?.connectDirectlyDescriptionUniversal) || ''}
                                </span>
                            </span>
                        </span>
                    </div>
                </label>
            </div>
        </>
    );
}
